// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║                           AUTH CONTEXT                                       ║
// ║              Global Authentication State Management                          ║
// ╚══════════════════════════════════════════════════════════════════════════════╝

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthAPI, TokenService } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we have tokens stored
        if (AuthAPI.isAuthenticated()) {
          const storedUser = AuthAPI.getStoredUser();

          // Immediately set user from localStorage for fast UI response
          if (storedUser) {
            setUser(storedUser);
          }

          // Try to verify with backend
          try {
            const response = await AuthAPI.getCurrentUser();
            if (response.success) {
              setUser(response.data);
              TokenService.setUser(response.data);
            }
          } catch (err) {
            // If error is 401, try to refresh the token
            if (err.status === 401) {
              const refreshToken = TokenService.getRefreshToken();
              if (refreshToken) {
                try {
                  const refreshResponse = await fetch(
                    `${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/auth/refresh`,
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ refreshToken }),
                    }
                  );

                  if (refreshResponse.ok) {
                    const data = await refreshResponse.json();
                    TokenService.setTokens(data.data.accessToken);

                    // Retry getting current user with new token
                    const retryResponse = await AuthAPI.getCurrentUser();
                    if (retryResponse.success) {
                      setUser(retryResponse.data);
                      TokenService.setUser(retryResponse.data);
                      return; // Success, exit early
                    }
                  }
                } catch (refreshErr) {
                  console.error('Token refresh failed:', refreshErr);
                }
              }
            }

            // If we reach here, session is truly invalid - clear and logout
            console.error('Session verification failed:', err);
            TokenService.clearTokens();
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    // Don't set global loading - let the component handle its own loading state
    try {
      const response = await AuthAPI.login(email, password);
      if (response.success) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: response.message };
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const register = useCallback(async (userData) => {
    setError(null);
    // Don't set global loading - let the component handle its own loading state
    try {
      const response = await AuthAPI.register(userData);
      if (response.success) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: response.message };
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await AuthAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const response = await AuthAPI.changePassword(currentPassword, newPassword);
      return { success: response.success, message: response.message };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to change password' };
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    changePassword,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
